import React, {
    useState, useEffect, useMemo,
} from 'react';
import { Language } from '../utils/Entities';
import { DEFAULT_LANGUAGE, LANGUAGE_SET, getLanguageByCode } from '../utils/Constants';

interface UIContextProviderProps {
  children: React.ReactNode;
}
interface UIContextAction {
  setDrawerOpen: (open: boolean)=> void;
  setCurrentLanguage: (language: Language)=> void;
  setDarkMode: (setDarkMode: boolean)=> void;
}
interface UIContextState {
  drawerOpen: boolean;
  currentLanguage: Language;
  darkMode: boolean;
}
export interface UIContext {
  state: UIContextState;
  actions: UIContextAction;
}

export const globalUIContext = React.createContext<UIContext | null>(null);

export const UIContextProvider = (props: UIContextProviderProps) => {
    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
    const [currentLanguage, setCurrentLanguage] = useState<Language>(DEFAULT_LANGUAGE);
    const [darkMode, setDarkMode] = useState<boolean>(() => {
        try {
            const stored = localStorage.getItem('theme');
            if (stored) {
                return stored === 'dark';
            }
            return true; // default to dark mode
        } catch {
            return true;
        }
    });

    useEffect(() => {
        try {
            const languagePreference = localStorage.getItem('lang_pref');
            if (languagePreference) {
                const preference = getLanguageByCode(LANGUAGE_SET, languagePreference);
                if (preference) {
                    setCurrentLanguage(preference);
                }
            }
        } catch {
            // localStorage unavailable
        }
    }, []);

    useEffect(() => {
        try {
            if (darkMode) {
                localStorage.setItem('theme', 'dark');
                document.documentElement.classList.add('dark');
            } else {
                localStorage.setItem('theme', 'light');
                document.documentElement.classList.remove('dark');
            }
        } catch {
            // localStorage unavailable
        }
    }, [darkMode]);

    const actions = useMemo(() => {
        return {
            setDrawerOpen,
            setCurrentLanguage,
            setDarkMode,
        };
    }, []);

    const valueObject = useMemo(() => {
        const state: UIContextState = {
            drawerOpen,
            currentLanguage,
            darkMode,
        };
        return {
            state,
            actions,
        };
    }, [drawerOpen, currentLanguage, darkMode, actions]);

    return (
        <globalUIContext.Provider value={valueObject}>
            {props.children}
        </globalUIContext.Provider>
    );
};
